import React, { Suspense, lazy, useEffect, useState } from 'react';

const SplineScene = lazy(() => import('./SplineScene'));

class SplineErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('[SplineErrorBoundary] Failed to render Spline scene', error, info);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }

    return this.props.children;
  }
}

const BackgroundFallback = () => (
  <div className="dashboard-background">
    <div className="spline-fallback">
      <div className="loading-orb" />
    </div>
  </div>
);

const AnimatedBackground = ({ isVisible }) => {
  if (!isVisible) {
    return <BackgroundFallback />;
  }

  return (
    <SplineErrorBoundary fallback={<BackgroundFallback />}>
      <Suspense fallback={<BackgroundFallback />}>
        <div className="dashboard-background">
          <SplineScene />
        </div>
      </Suspense>
    </SplineErrorBoundary>
  );
};

const APPLICATIONS = [
  {
    id: 'cliente-manager',
    icon: '👥',
    name: 'APP - Cartas Maravilha',
    description:
      'Distribuição inteligente de clientes por equipa comercial com geração de listas otimizadas para campanhas de mailing.',
    accentColor: '#0066FF',
    accentGlow: 'rgba(0, 102, 255, 0.25)',
    features: [
      'Balanceamento automático entre comerciais',
      'Segmentação dinâmica por prioridades de campanha',
      'Relatórios de cobertura e follow-up em tempo real',
    ],
  },
  {
    id: 'commission-pro',
    icon: '💰',
    name: 'Commission Pro',
    description:
      'Motor avançado de comissionamento com processamento automático de dados Bitrix, cálculo de comissões e integração com relatórios.',
    accentColor: '#00CC66',
    accentGlow: 'rgba(0, 204, 102, 0.25)',
    features: [
      'Upload e processamento de CSV do Bitrix',
      'Filtro automático de fases "Ok" e "Enviado"',
      'Integração com report.xlsx e cálculo de comissões',
    ],
  },
  {
    id: 'meo-dealer',
    icon: '📊',
    name: 'MEO - Dealer',
    description:
      'Auditoria de Comissões MEO: Processa, cruza dados e serve relatório interativo de negócios fechados e comissões.',
    accentColor: '#FF6600',
    accentGlow: 'rgba(255, 102, 0, 0.25)',
    features: [
      'Cruzamento automático de ficheiros DEAL e Dealer',
      'Dashboards interativos de análise de comissões',
      'Alertas para comissões não pagas ou débitos',
    ],
  },
];

const useSplineVisibility = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 900px)');

    const updateVisibility = (event) => {
      setIsVisible(event.matches);
    };

    // Initial value
    updateVisibility(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateVisibility);
      return () => mediaQuery.removeEventListener('change', updateVisibility);
    }

    // Fallback for older browsers
    mediaQuery.addListener(updateVisibility);
    return () => mediaQuery.removeListener(updateVisibility);
  }, []);

  return isVisible;
};

export const Dashboard = ({ onLogout, onSelectApp }) => {
  const canRenderSpline = useSplineVisibility();
  console.log("Dashboard component rendering...");

  return (
    <div className="dashboard-container">
      <AnimatedBackground isVisible={canRenderSpline} />

      <header className="dashboard-header">
        <img
          className="dashboard-logo"
          src="https://customer-assets.emergentagent.com/job_26ffda92-be08-4603-8924-7735023cc582/artifacts/xriyhnb8_Logotipo-UMTS-no-Background_1080x1080.webp"
          alt="Logotipo UMTS"
        />

        <div className="header-content">
          <h1 className="dashboard-title">
            UMTS <span className="title-highlight">Technology Labs</span>
          </h1>
          <p className="dashboard-subtitle">Portal de aplicações empresariais</p>
        </div>

        <button type="button" className="logout-button" onClick={onLogout}>
          Sair
        </button>
      </header>

      <main className="dashboard-content">
        <div className="section-intro">
          <h2 className="section-heading">Aplicações Disponíveis</h2>
          <p className="section-description">
            A suite operacional da UMTS integra as principais ferramentas para gestão de clientes,
            performance comercial e inteligência de negócio.
          </p>
        </div>

        <div className="applications-grid">
          {APPLICATIONS.map((app) => (
            <article
              key={app.id}
              className="application-card"
              style={{
                '--accent-color': app.accentColor,
                '--accent-glow': app.accentGlow,
              }}
            >
              <div className="app-header">
                <span className="app-icon" aria-hidden="true">
                  {app.icon}
                </span>
                <div className="app-status">
                  <span className="status-dot" />
                  ativo
                </div>
              </div>

              <h3 className="app-name">{app.name}</h3>
              <p className="app-description">{app.description}</p>

              <div className="app-features">
                {app.features.map((feature) => (
                  <div key={feature} className="feature-item">
                    <span className="feature-bullet" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <div className="app-footer">
                <button
                  type="button"
                  className="access-button"
                  onClick={() => {
                    console.log(`Access button clicked for app: ${app.id}`);
                    if (typeof onSelectApp === 'function') {
                      onSelectApp(app.id);
                    } else {
                      console.error('onSelectApp is not a function!');
                    }
                  }}
                >
                  Aceder
                </button>
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
};